import React, { useState, useEffect, useRef, useCallback } from 'react';
import PersonalInfo from './steps/PersonalInfo';
import ContactInfo from './steps/ContactInfo';
import Insurance from './steps/Insurance';
import Pharmacy from './steps/Pharmacy';
import Medications from './steps/Medications';
import Doctors from './steps/Doctors';
import BenefitsIncome from './steps/BenefitsIncome';
import Review from './steps/Review';
import ScopeOfAppointment from './steps/ScopeOfAppointment';
import SoaAgentSection from './steps/SoaAgentSection';
import ResumeGate from './ResumeGate';
import AccessGate from './AccessGate';
import { ageInYears, dobProblem } from '../utils/dob';
import { fillBlanks } from '../utils/prefill';
import { signatureProblem } from '../utils/signature';

const DEFAULT_FORM_TYPE = 'medicare-intake';

// Registry of every step, keyed by the token used in FORM_TYPES below.
const STEP_REGISTRY = {
  soa:          { label: 'Scope of Appointment', component: ScopeOfAppointment },
  agentSection: { label: 'Agent Section',        component: SoaAgentSection },
  contact:      { label: 'Contact Info',         component: ContactInfo },
  personal:     { label: 'Personal Info',        component: PersonalInfo },
  benefits:     { label: 'Benefits',             component: BenefitsIncome },
  insurance:    { label: 'Insurance',            component: Insurance },
  pharmacy:     { label: 'Pharmacy',             component: Pharmacy },
  drugs:        { label: 'Medications',          component: Medications },
  doctors:      { label: 'Doctors',              component: Doctors },
  review:       { label: 'Review',               component: Review },
};

// Each formType is just the ordered list of step tokens it needs.
// 'existing' is a flag (not a step) — it switches the Contact Info step into
// read-only "confirm this is still correct" mode instead of a blank entry form.
// To add a new variant, add one array here — nothing else needs to change.
const FORM_TYPES = {
  'medicare-packet':      ['soa', 'contact', 'personal', 'benefits', 'insurance', 'pharmacy', 'drugs', 'doctors', 'review'],
  [DEFAULT_FORM_TYPE]:    ['contact', 'personal', 'benefits', 'insurance', 'pharmacy', 'drugs', 'doctors', 'review'],
  'drug-plan-review':     ['soa', 'existing', 'contact', 'pharmacy', 'drugs', 'review'],
  'mapd-review':          ['soa', 'existing', 'contact', 'pharmacy', 'drugs', 'doctors', 'review'],
  'scope-of-appointment': ['soa'],
  'soa-agent':            ['agentSection'],
};

// The URL's formType picks which steps to show (matched against FORM_TYPES
// above) - that's a frontend-only vocabulary, not what TI_Forms_Controller's
// /forms/submit expects. They're the same string for drug-plan-review/
// mapd-review, but diverge for the SOA-only/packet forms - translate before
// ever sending formType to the backend.
// Entries that actually hold something - medications/doctors by name,
// pharmacies by name in either their object or legacy string form.
function activeList(list) {
  return (list || []).filter(item => item && (typeof item === 'string' ? item.trim() : (item.name || item.Pharmacy_Name)));
}

function toControllerFormType(feFormType) {
  const map = {
    'scope-of-appointment': 'medicare-soa',
    'medicare-packet': 'medicare-intake-with-soa',
  };
  return map[feFormType] || feFormType || DEFAULT_FORM_TYPE;
}

function getParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    recordId:      params.get('recordId') || null,
    contactId:     params.get('cid') || null,
    opportunityId: params.get('oid') || null,
    documentId:    params.get('documentId') || null,
    inviteToken:   params.get('t') || null,
    mode:          params.get('mode') || null,
    formType:      params.get('formType') || null,
  };
}

function buildSteps(formType) {
  const tokens = FORM_TYPES[formType] || FORM_TYPES[DEFAULT_FORM_TYPE];
  const contactMode = tokens.includes('existing') ? 'verify' : 'entry';
  return tokens
    .filter(t => t !== 'existing')
    .map(t => (t === 'contact' ? { ...STEP_REGISTRY[t], contactMode } : STEP_REGISTRY[t]));
}

const initialData = {
  // Personal Info
  firstName: '', middleName: '', lastName: '',
  dob: '', ssn: '', height: '', weight: '', gender: '',
  // Contact Info
  address: '', address2: '', city: '', county: '', state: '', zip: '',
  mailingIsSameAsPhysical: true,
  mailingAddress: '', mailingAddress2: '', mailingCity: '', mailingState: '', mailingZip: '',
  email: '', phone: '',
  // Insurance
  medicareNumber: '', partADate: '', partBDate: '',
  medicaidNumber: '', federalLIS: '',
  isMarried: null,
  isVeteran: null, vaRx: null, hasCurrentPlan: null, hasPrivateInsurance: null, isSmoker: null,
  estimatedEligibility: '',
  pharmacies: [],
  incomeSource: [], otherIncome: '', totalMonthlyIncome: '',
  preferredProviders: [''],
  // Medications
  medications: [],
  // Doctors
  doctors: [],
  // Review
  additionalComments: '',
  // Scope of Appointment — client
  soaProducts: [],
  soaProductTimestamps: {},
  soaIsAuthorizedRep: false,
  soaRepName: '', soaRepRelationship: '',
  soaSignature: '',
  soaAgreedToElectronicSignature: false,
  soaConsentTimestamp: null,
  // Scope of Appointment — agent
  soaContactMethod: '',
  soaAgentName: '', soaAgentPhone: '',
  soaPlansRepresented: '',
  soaAppointmentDate: '',
  soaLateExplanation: '',
  soaAgentSignature: '',
  soaAgentAgreedToElectronicSignature: false,
  soaAgentConsentTimestamp: null,
};

export default function IntakeWizard() {
  const { recordId, contactId, opportunityId, documentId, inviteToken, mode, formType } = getParams();
  const isResumeMode = mode === 'resume' && !!inviteToken;
  const STEPS = buildSteps(formType);
  const isSoaOnly = formType === 'scope-of-appointment';
  const isSoaAgent = formType === 'soa-agent';
  const storageKey = documentId ? `medicare_intake_soa_${documentId}` : inviteToken ? `medicare_intake_invite_${inviteToken}` : contactId ? `medicare_intake_${contactId}` : recordId ? `medicare_intake_${recordId}` : 'medicare_intake_draft';
  const stateStorageKey = `${storageKey}_state`;

  const [step, setStep] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(stateStorageKey) || '{}');
      return Number.isInteger(saved.step) ? saved.step : 0;
    } catch {
      return 0;
    }
  });
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return initialData;
      const parsed = JSON.parse(saved);
      const normalizeProviders = arr => {
        const trimmed = (arr || []).filter((v, i, a) => v || i === a.length - 1);
        return trimmed.length > 0 ? trimmed : [''];
      };
      const normalizePharmacies = arr =>
        (arr || [])
          .map(p => typeof p === 'string' ? { Pharmacy_Name: p, Pharmacy_NPI: null, Pharmacy_Address: '', Pharmacy_City: '', Pharmacy_State: '' } : p)
          .filter(p => p && (p.Pharmacy_Name || p.name || '').trim());
      return {
        ...initialData,
        ...parsed,
        phone: parsed.phone || parsed.cellPhone || parsed.homePhone || '',
        preferredProviders: normalizeProviders(parsed.preferredProviders),
        pharmacies: normalizePharmacies(parsed.pharmacies),
      };
    } catch {
      return initialData;
    }
  });
  const [submitted, setSubmitted] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Unsaved text a step is still holding in its own local inputs (the
  // Doctors step's search/manual-entry boxes) - see Doctors.js's
  // describePendingInput. Blocks Next until it's added or cleared.
  const [pendingStepInput, setPendingStepInput] = useState('');
  const shownPendingInputRef = useRef('');
  // Keep the warning banner in sync once it's showing: it updates as they
  // keep typing and disappears once they add the doctor or clear the boxes.
  useEffect(() => {
    setSubmitError(err => {
      if (!err || err !== shownPendingInputRef.current) return err;
      shownPendingInputRef.current = pendingStepInput;
      return pendingStepInput || null;
    });
  }, [pendingStepInput]);
  const [soaSubmitted, setSoaSubmitted] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(stateStorageKey) || '{}');
      return Boolean(saved.soaSubmitted);
    } catch {
      return false;
    }
  });
  const [soaSubmitting, setSoaSubmitting] = useState(false);
  const [agentLoading, setAgentLoading] = useState(isSoaAgent && !!documentId);
  const [prefillLoading, setPrefillLoading] = useState(!isResumeMode && !isSoaAgent && !!inviteToken);
  // No link the app ever sends actually carries ?mode=resume (Save and
  // Finish Later doesn't send a new link at all - see handleSaveDraft) -
  // the ORIGINAL invite link is what a client re-opens to come back. So
  // resumeAuthorized/showResumeGate can't just key off isResumeMode; a
  // draft-session check (below) decides whether re-opening the plain
  // invite link should gate behind ResumeGate's email+code verification.
  const [resumeAuthorized, setResumeAuthorized] = useState(isSoaAgent || !inviteToken);
  const [showResumeGate, setShowResumeGate] = useState(isResumeMode);
  const [draftCheckDone, setDraftCheckDone] = useState(isResumeMode || isSoaAgent || !inviteToken);
  // Every form requires a verified invite token to open, except the agent's
  // SOA-completion link (isSoaAgent, keyed off documentId instead) and resume
  // mode (which has its own email+code verification via ResumeGate).
  const [accessDenied, setAccessDenied] = useState(!isResumeMode && !isSoaAgent && !inviteToken);
  const [agentLinkExpired, setAgentLinkExpired] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [expired, setExpired] = useState(false);

  const warnTimerRef    = useRef(null);
  const expireTimerRef  = useRef(null);
  const countdownRef    = useRef(null);

  const TIMEOUT_MS = 15 * 60 * 1000;
  const WARN_MS    =  2 * 60 * 1000;

  const handleExpire = useCallback(() => {
    clearInterval(countdownRef.current);
    if (!soaSubmitted) {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(stateStorageKey);
    }
    setExpired(true);
    setShowTimeoutWarning(false);
  }, [soaSubmitted, storageKey, stateStorageKey]);

  const resetTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(expireTimerRef.current);
    clearInterval(countdownRef.current);
    setShowTimeoutWarning(false);
    setSecondsLeft(120);

    warnTimerRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setSecondsLeft(120);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdownRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARN_MS);

    expireTimerRef.current = setTimeout(handleExpire, TIMEOUT_MS);
  }, [handleExpire, TIMEOUT_MS, WARN_MS]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimers));
      clearTimeout(warnTimerRef.current);
      clearTimeout(expireTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [resetTimers]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(formData));
    } catch {}
  }, [formData, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(stateStorageKey, JSON.stringify({ step, soaSubmitted }));
    } catch {}
  }, [stateStorageKey, step, soaSubmitted]);

  useEffect(() => {
    if (!isSoaAgent || !documentId) return;

    let cancelled = false;
    async function loadSoa() {
      try {
        const res = await fetch(`/api/get-soa?documentId=${encodeURIComponent(documentId)}`);
        const payload = await res.json();
        if (!res.ok) {
          if (res.status === 409) {
            setAgentLinkExpired(true);
            setSubmitError(payload.error || 'This Scope of Appointment has already been completed.');
            return;
          }
          throw new Error(payload.error || `SOA lookup failed (${res.status})`);
        }
        if (cancelled) return;
        setFormData(prev => ({
          ...prev,
          ...(payload.clientData || {}),
          ...(payload.agentPrefill || {}),
          // get-soa.js's agentPrefill is currently always {} (a stub), but
          // the agent's name is already available under clientData.agentName
          // - it's whatever was set on the invite, carried through since
          // it's an "operational" field (see field-classification.ts) that
          // rides along in the client's own submission payload. soaAgentName
          // is the field the form actually reads/submits, so it needs to be
          // seeded from agentName explicitly - they're different keys.
          soaAgentName: payload.clientData?.agentName || prev.soaAgentName || '',
          soaAgentPhone: payload.agentPrefill?.soaAgentPhone || prev.soaAgentPhone || '(503) 324-4511',
          contactId: payload.contactId || payload.clientData?.contactId || prev.contactId,
          recordId: payload.contactId || payload.clientData?.recordId || payload.clientData?.contactId || prev.recordId,
          opportunityId: payload.dealId || payload.clientData?.opportunityId || prev.opportunityId,
          dealId: payload.dealId || payload.clientData?.dealId || prev.dealId,
          documentId,
        }));
      } catch (err) {
        if (!cancelled) setSubmitError(err.message);
      } finally {
        if (!cancelled) setAgentLoading(false);
      }
    }

    loadSoa();
    return () => { cancelled = true; };
  }, [isSoaAgent, documentId]);

  // Re-opening the plain invite link (no ?mode=resume - nothing ever sends
  // that) still needs to gate on ResumeGate's email+code verification if a
  // "Save and Finish Later" draft exists for this token, rather than
  // silently loading the blank/original-invite prefill instead.
  useEffect(() => {
    if (isResumeMode || isSoaAgent || !inviteToken) return;
    let cancelled = false;
    async function checkDraftSession() {
      try {
        const res = await fetch(`/api/intake-draft-session?t=${encodeURIComponent(inviteToken)}`);
        if (cancelled) return;
        if (res.ok) {
          setShowResumeGate(true);
        } else {
          setResumeAuthorized(true);
        }
      } catch {
        if (!cancelled) setResumeAuthorized(true);
      } finally {
        if (!cancelled) setDraftCheckDone(true);
      }
    }
    checkDraftSession();
    return () => { cancelled = true; };
  }, [isResumeMode, isSoaAgent, inviteToken]);

  useEffect(() => {
    if (!draftCheckDone || showResumeGate || isSoaAgent || !inviteToken) return;

    let cancelled = false;
    async function loadPrefill() {
      try {
        const res = await fetch(`/api/prefill?t=${encodeURIComponent(inviteToken)}`);
        if (!res.ok) throw new Error(`Invite prefill failed (${res.status})`);
        const payload = await res.json();
        if (cancelled) return;
        const clientData = payload.clientData || {};
        if (payload.videoUrl) setVideoUrl(payload.videoUrl);
        // This runs on every page load, including when a client comes back to
        // a form they'd already started (progress is restored from
        // localStorage first). Zoho's on-file info only fills fields that are
        // still empty - it used to overwrite everything, silently replacing a
        // new address the client had typed with the old one from Zoho.
        setFormData(prev => ({
          ...prev,
          ...fillBlanks(prev, clientData),
          phone: prev.phone || clientData.phone || clientData.cellPhone || clientData.homePhone || payload.phone || '',
          contactId: payload.contactId || clientData.contactId || prev.contactId,
          recordId: payload.contactId || clientData.recordId || clientData.contactId || prev.recordId,
          opportunityId: payload.dealId || clientData.opportunityId || prev.opportunityId,
          dealId: payload.dealId || clientData.dealId || prev.dealId,
          inviteToken,
        }));

        // Some CRM contact records have the rest of the address but no
        // county on file. Silently backfill it via the same geocoding
        // lookup AddressAutocomplete uses, rather than blocking the user
        // on a field they were never shown and never typed themselves
        // (this is the field validateStep('Contact Info') requires).
        if (!clientData.county && clientData.address && clientData.city && clientData.state) {
          try {
            const q = [clientData.address, clientData.city, clientData.state, clientData.zip].filter(Boolean).join(', ');
            const geoRes = await fetch(`/api/address-search?q=${encodeURIComponent(q)}`);
            if (geoRes.ok) {
              const results = await geoRes.json();
              const county = results?.[0]?.county;
              if (county && !cancelled) {
                setFormData(prev => (prev.county ? prev : { ...prev, county }));
              }
            }
          } catch {
            // Best-effort only - if this fails, validateStep still catches
            // the missing county and the user can fill it in via Update.
          }
        }
      } catch (err) {
        console.warn('Invite prefill failed', err);
        if (!cancelled) setAccessDenied(true);
      } finally {
        if (!cancelled) setPrefillLoading(false);
      }
    }

    loadPrefill();
    return () => { cancelled = true; };
  }, [draftCheckDone, showResumeGate, isSoaAgent, inviteToken]);

  function handleResumeAuthorized(data) {
    setFormData(prev => ({
      ...prev,
      ...(data.formData || {}),
      phone: data.formData?.phone || data.formData?.cellPhone || data.formData?.homePhone || prev.phone || '',
      draftToken: inviteToken,
      formType: data.formType || prev.formType || 'medicare-intake',
      personalCreatorId: data.personalCreatorId || prev.personalCreatorId,
      medicalCreatorId: data.medicalCreatorId || prev.medicalCreatorId,
    }));
    // verifyDraftCode already returns the actual step the draft was saved
    // from (invite.draftStep) - this used to ignore it and always jump to
    // Personal Info instead, so resuming lost the user's place in the
    // wizard even though their data came back fine. Clamped defensively in
    // case STEPS is shorter than the saved index for any reason.
    const savedStep = Number.isInteger(data.step) ? data.step : 0;
    setStep(Math.min(Math.max(savedStep, 0), STEPS.length - 1));
    setPrefillLoading(false);
    setResumeAuthorized(true);
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function clientMeta() {
    return {
      userAgent: navigator.userAgent || 'unknown',
      language: navigator.language || '',
      platform: navigator.platform || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    };
  }

  function validateStep(label) {
    // Nothing previously stopped "Next" on the SOA step itself - handleNext
    // fires /api/submit-soa the instant this step is left, regardless of
    // whether the fields ScopeOfAppointment.js actually shows as required
    // (signature disabled until the agreement checkbox is checked, etc.)
    // were ever filled in. On a real submission, that let a premature,
    // blank submit-soa call go through (signature '', agreed false) and
    // create its own real workflow/documentId, and then a second, genuine
    // one a few minutes later after the client actually signed - two
    // entirely separate, unrelated workflows for one appointment, not a
    // duplicate of the same one (the existing documentId-reuse/continuation
    // logic never had anything to reuse, since the client only ever sends
    // the SAME already-known documentId back on calls after the first).
    if (label === 'Scope of Appointment') {
      if (!Array.isArray(formData.soaProducts) || formData.soaProducts.length === 0) {
        return 'Select at least one product for the agent to discuss.';
      }
      if (!formData.soaAgreedToElectronicSignature) {
        return 'Please agree to use an electronic signature before continuing.';
      }
      if (!String(formData.soaSignature || '').trim()) {
        return 'Please type your full legal name as the signature.';
      }
      if (formData.soaIsAuthorizedRep) {
        if (!String(formData.soaRepName || '').trim()) return 'Representative name is required.';
        if (!String(formData.soaRepRelationship || '').trim()) return 'Representative relationship is required.';
      }
    }
    // An empty list used to pass silently, so agents couldn't tell "client
    // has none" from "client skipped it". Require an explicit answer.
    if (label === 'Medications' && activeList(formData.medications).length === 0 && !formData.noMedications) {
      return 'Please add your prescription medications, or check "I don\'t take any prescription medications" to continue.';
    }
    if (label === 'Pharmacy' && activeList(formData.pharmacies).length === 0 && !formData.noPharmacies) {
      return 'Please add your pharmacy, or check "I don\'t use a pharmacy" to continue.';
    }
    if (label === 'Doctors' && activeList(formData.doctors).length === 0 && !formData.noDoctors) {
      return 'Please add your doctors, or check "I don\'t have any doctors to add" to continue.';
    }
    if (label === 'Personal Info') {
      if (!String(formData.firstName || '').trim()) return 'First name is required.';
      if (!String(formData.lastName || '').trim()) return 'Last name is required.';
      if (!String(formData.dob || '').trim()) return 'Date of birth is required.';
      // Under 18 can't be right even for disability Medicare; also catches
      // the phone date picker's "today" default and typos like 2062.
      const dobError = dobProblem(formData.dob, 18);
      if (dobError) return dobError;
    }
    if (label === 'Contact Info') {
      if (!String(formData.address || '').trim()) return 'Address is required.';
      if (!String(formData.city || '').trim()) return 'City is required.';
      if (!String(formData.state || '').trim()) return 'State is required.';
      if (!String(formData.zip || '').trim()) return 'ZIP code is required.';
      if (!String(formData.county || '').trim()) return 'County is required.';
      if (!String(formData.email || '').trim()) return 'Email address is required.';
      if (!String(formData.phone || '').trim()) return 'Phone number is required.';
    }
    return '';
  }

  async function handleNext() {
    if (pendingStepInput) {
      shownPendingInputRef.current = pendingStepInput;
      setSubmitError(pendingStepInput);
      return;
    }
    const validationError = validateStep(STEPS[step].label);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError(null);
    // 18-64 is possible (Medicare through disability) but unusual - make the
    // client confirm rather than block them.
    if (STEPS[step].label === 'Personal Info') {
      const age = ageInYears(formData.dob);
      if (age !== null && age < 65 && !window.confirm(
        `The date of birth you entered (${formData.dob.slice(5, 7)}/${formData.dob.slice(8, 10)}/${formData.dob.slice(0, 4)}) means you're ${age} years old. `
        + 'Medicare before age 65 is usually through disability.\n\n'
        + 'Click OK if your date of birth is correct, or Cancel to fix it.',
      )) {
        return;
      }
    }
    if (STEPS[step].label === 'Scope of Appointment') {
      setSoaSubmitting(true);
      const crmContactId = formData.contactId || contactId || recordId;
      const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
      const payload = {
        ...formData,
        recordId: formData.recordId || crmContactId,
        contactId: crmContactId,
        cid: crmContactId,
        opportunityId: crmDealId,
        dealId: formData.dealId || crmDealId,
        oid: crmDealId,
        formType: toControllerFormType(formType),
        inviteToken: inviteToken || formData.inviteToken,
        soaMode: 'client',
        soaSignatureDate: new Date().toLocaleDateString('en-US'),
        ...clientMeta(),
      };
      try {
        const res = await fetch('/api/submit-soa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`SOA submission failed (${res.status}). Please try again.`);
        const soaResult = await res.json().catch(() => ({}));
        if (soaResult.documentId || soaResult.agentUrl) {
          setFormData(prev => ({
            ...prev,
            ...(soaResult.clientData || {}),
            documentId: soaResult.documentId || prev.documentId,
            soaDocumentId: soaResult.documentId || prev.soaDocumentId,
            agentUrl: soaResult.agentUrl || prev.agentUrl,
            recordId: prev.recordId || soaResult.clientData?.recordId || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            contactId: prev.contactId || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            cid: prev.cid || soaResult.clientData?.contactId || soaResult.contactId || crmContactId,
            opportunityId: prev.opportunityId || soaResult.clientData?.opportunityId || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
            dealId: prev.dealId || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
            oid: prev.oid || soaResult.clientData?.dealId || soaResult.dealId || crmDealId,
          }));
        }
        setSoaSubmitted(true);
      } catch (err) {
        setSubmitError(err.message);
        setSoaSubmitting(false);
        return;
      }
      setSoaSubmitting(false);
    }
    setStep(st => st + 1);
  }

  // Skips past a signed SOA step; returns -1 if there's nowhere valid to
  // land (SOA already signed and was the only step before `fromStep`).
  function computePrevStep(fromStep) {
    let prev = fromStep - 1;
    while (prev >= 0 && soaSubmitted && STEPS[prev].label === 'Scope of Appointment') {
      prev--;
    }
    return prev;
  }

  function handleBack() {
    const prev = computePrevStep(step);
    // Math.max(0, prev) used to clamp a negative result back to index 0,
    // which IS the signed SOA step, defeating the lock - do nothing instead.
    if (prev < 0) return;
    setStep(prev);
  }

  async function handleSubmit() {
    if (isSoaAgent) {
      const agentError = !formData.soaAgentAgreedToElectronicSignature
        ? 'Please agree to use an electronic signature before submitting.'
        : signatureProblem(formData.soaAgentSignature);
      if (agentError) {
        setSubmitError(agentError);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError(null);

    const crmContactId = formData.contactId || contactId || recordId;
    const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
    const payload = {
      ...formData,
      // "+ Add Provider" leaves empty rows behind when clicked by accident;
      // don't store them as if they were answers.
      preferredProviders: (formData.preferredProviders || []).filter(p => String(p || '').trim()),
      // A "none" answer only counts while its list is actually empty (the
      // box is hidden once something is added, but its old value lingers).
      noMedications: activeList(formData.medications).length === 0 && formData.noMedications === true,
      noPharmacies: activeList(formData.pharmacies).length === 0 && formData.noPharmacies === true,
      noDoctors: activeList(formData.doctors).length === 0 && formData.noDoctors === true,
      recordId: formData.recordId || crmContactId,
      contactId: crmContactId,
      cid: crmContactId,
      opportunityId: crmDealId,
      dealId: formData.dealId || crmDealId,
      oid: crmDealId,
      formType: toControllerFormType(formType),
      documentId: formData.documentId || documentId,
      soaDocumentId: formData.soaDocumentId || formData.documentId || documentId,
      inviteToken: inviteToken || formData.inviteToken,
      soaSignatureDate: new Date().toLocaleDateString('en-US'),
      ...clientMeta(),
    };

    try {
      if (!isSoaOnly && !isSoaAgent) {
        const res = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result.ok === false) {
          const detail = result.data?.raw || result.data?.error || result.detail || result.error;
          throw new Error(detail || `Intake submission failed (${res.status}). Please try again.`);
        }
      }

      const hasSoaStep = STEPS.some(st => st.label === 'Scope of Appointment');
      if ((hasSoaStep && !soaSubmitted) || isSoaOnly || isSoaAgent) {
        const res = await fetch('/api/submit-soa', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            soaMode: isSoaAgent ? 'agent' : 'client',
          }),
        });
        if (!res.ok) throw new Error(`SOA submission failed (${res.status}). Please try again.`);
      }

      localStorage.removeItem(storageKey);
      localStorage.removeItem(stateStorageKey);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // videoUrl only ever comes back from /api/prefill for medicare-intake and
  // medicare-intake-with-soa (see TI_Forms_Controller's
  // VIDEO_ELIGIBLE_FORM_TYPES) - isSoaOnly/isSoaAgent sessions never have it
  // set, so no extra gating is needed here beyond "is it present". Short
  // delay + visible fallback link rather than an instant window.location
  // swap, so the redirect doesn't yank the page out from under someone who
  // didn't expect it (and still works if the browser blocks the auto-nav).
  useEffect(() => {
    if (!submitted || !videoUrl) return;
    const timer = setTimeout(() => {
      window.location.href = videoUrl;
    }, 2500);
    return () => clearTimeout(timer);
  }, [submitted, videoUrl]);

  async function handleSaveDraft() {
    setSavingDraft(true);
    setSubmitError(null);

    const crmContactId = formData.contactId || contactId || recordId;
    const crmDealId = formData.opportunityId || opportunityId || formData.dealId;
    const payload = {
      ...formData,
      recordId: formData.recordId || crmContactId,
      contactId: crmContactId,
      cid: crmContactId,
      opportunityId: crmDealId,
      dealId: formData.dealId || crmDealId,
      oid: crmDealId,
      currentStep: step,
      formType: toControllerFormType(formType || formData.formType),
      inviteToken: inviteToken || formData.inviteToken,
      draftToken: formData.draftToken,
      personalCreatorId: formData.personalCreatorId,
      medicalCreatorId: formData.medicalCreatorId,
      agentEmail: formData.agentEmail,
      agentName: formData.agentName,
      submittedAt: new Date().toISOString(),
      ...clientMeta(),
    };

    try {
      const res = await fetch('/api/save-intake-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.error || result.detail || `Save failed (${res.status}).`);
      }
      setFormData(prev => ({
        ...prev,
        draftToken: result.draftToken || prev.draftToken,
        personalCreatorId: result.personalCreatorId || prev.personalCreatorId,
        medicalCreatorId: result.medicalCreatorId || prev.medicalCreatorId,
      }));
      setDraftSaved(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSavingDraft(false);
    }
  }

  const saveAllowedLabels = new Set(['Insurance', 'Pharmacy', 'Medications', 'Doctors', 'Review']);
  const showSaveDraft = !isSoaOnly && !isSoaAgent && saveAllowedLabels.has(STEPS[step].label);

  if (expired) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏱</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Session Expired</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Your session has expired due to inactivity. For your security, your form data has been cleared.
              Please reload the page to start over.
            </p>
            <button style={{ ...s.btnNext, marginTop: 8 }} onClick={() => window.location.reload()}>
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResumeGate && !resumeAuthorized) {
    return <ResumeGate token={inviteToken} onAuthorized={handleResumeAuthorized} />;
  }

  if (submitted) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>
              {isSoaOnly ? 'Scope of Appointment Submitted' : 'Thank You!'}
            </h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              {isSoaAgent
                ? 'The agent section of the Scope of Appointment has been submitted successfully.'
                : isSoaOnly
                ? 'Your Scope of Appointment has been submitted. Your agent will be in touch shortly.'
                : 'Your Medicare intake form has been submitted successfully. Our team will be in touch with you shortly.'}
            </p>
            {videoUrl && (
              <p style={{ color: '#4a6288', lineHeight: 1.6, marginTop: 20 }}>
                Taking you to a short Medicare 101 video to watch before your appointment…
                <br />
                <a href={videoUrl} style={{ color: '#1a3364', fontWeight: 600 }}>
                  Click here if you're not redirected automatically
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (draftSaved) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Saved</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Use the same link from your original invite email to come back and finish - you'll be asked to confirm your email address before the form reloads.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (agentLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Loading Scope of Appointment</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Pulling in the client signature record for the agent section.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (agentLinkExpired) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Scope of Appointment Already Completed</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              This agent signing link has already been used. No additional signature is needed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (prefillLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <h2 style={{ color: '#1a3364', marginTop: 0 }}>Loading Form</h2>
            <p style={{ color: '#4a6288', lineHeight: 1.6 }}>
              Loading the secure invite information for this form.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <AccessGate />;
  }

  const StepComponent = STEPS[step].component;
  const isLast = step === STEPS.length - 1;
  const titleLabel = isSoaAgent ? 'SCOPE OF APPOINTMENT — AGENT' : isSoaOnly ? 'SCOPE OF APPOINTMENT' : 'MEDICARE CLIENT INTAKE FORM';

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <img src="/logo.png" alt="Translating Insurance" style={s.logoImg} />
      </div>

      {/* Title bar */}
      <div style={s.titleBar}>{titleLabel}</div>

      {/* Progress bar */}
      <div style={s.progressRow}>
        {STEPS.map((st, i) => (
          <React.Fragment key={st.label}>
            <div style={s.progressItem}>
              <button
                type="button"
                onClick={() => !(soaSubmitted && st.label === 'Scope of Appointment') && setStep(i)}
                style={{
                  ...s.dot,
                  background: i < step ? '#1a3364' : i === step ? '#2a5298' : '#b8cfe8',
                  boxShadow: i === step ? '0 0 0 3px #dbeef8, 0 0 0 5px #2a5298' : 'none',
                  border: 'none',
                  cursor: soaSubmitted && st.label === 'Scope of Appointment' ? 'default' : 'pointer',
                  opacity: soaSubmitted && st.label === 'Scope of Appointment' ? 0.6 : 1,
                }}
              >
                {i < step ? '✓' : i + 1}
              </button>
              <span style={{
                ...s.dotLabel,
                color: i === step ? '#1a3364' : i < step ? '#2a5298' : '#8faac5',
                fontWeight: i === step ? 700 : 400,
              }}>
                {st.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ ...s.connector, background: i < step ? '#1a3364' : '#b8cfe8' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step card */}
      <div style={s.card}>
        <h3 style={s.stepTitle}>{STEPS[step].label}</h3>
        <StepComponent
          data={formData}
          onChange={handleChange}
          mode={STEPS[step].contactMode}
          onPendingInputChange={setPendingStepInput}
          hasDoctorsStep={STEPS.some(st => st.label === 'Doctors')}
          hasMedicationsStep={STEPS.some(st => st.label === 'Medications')}
        />
      </div>

      {/* Navigation */}
      <div style={s.navRow}>
        <div>
          {step > 0 && computePrevStep(step) >= 0 && (
            <button style={s.btnBack} onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
        {showSaveDraft && (
          <button
            type="button"
            style={{ ...s.btnSaveDraft, opacity: (savingDraft || submitting) ? 0.7 : 1 }}
            onClick={handleSaveDraft}
            disabled={savingDraft || submitting}
          >
            {savingDraft && <span className="btn-spinner" />}
            {savingDraft ? 'Saving...' : 'Save and Finish Later'}
          </button>
        )}
        <div>
          {!isLast ? (
            <button style={{ ...s.btnNext, opacity: soaSubmitting ? 0.7 : 1 }} onClick={handleNext} disabled={soaSubmitting}>
              {soaSubmitting ? 'Saving…' : 'Next →'}
            </button>
          ) : (
            <button
              style={{ ...s.btnNext, opacity: (submitting || savingDraft) ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={submitting || savingDraft}
            >
              {submitting && <span className="btn-spinner" />}
              {submitting ? 'Submitting…' : 'Submit Form'}
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <div style={{ color: '#c0392b', background: '#fdf0ef', border: '1px solid #f5c6c2', borderRadius: 8, padding: '10px 16px', marginTop: 8, fontSize: 14 }}>
          {submitError}
        </div>
      )}

      {showTimeoutWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3364', color: '#fff', borderRadius: 10,
          padding: '14px 24px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)', fontSize: 14, whiteSpace: 'nowrap',
        }}>
          <span>Your session will expire in <strong>{secondsLeft}s</strong> due to inactivity.</span>
          <button
            onClick={resetTimers}
            style={{
              background: '#fff', color: '#1a3364', border: 'none',
              borderRadius: 6, padding: '6px 16px', fontWeight: 700,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Still Here
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  container: {
    width: '100%',
    maxWidth: 700,
    margin: '0 auto',
    paddingTop: 32,
  },
  header: {
    textAlign: 'center',
    marginBottom: 12,
  },
  logoImg: {
    maxWidth: 340,
    width: '100%',
    height: 'auto',
  },
  titleBar: {
    background: '#1a3364',
    borderRadius: 8,
    padding: '13px 24px',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.08em',
    marginBottom: 24,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
    padding: '0 4px',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flex: '0 0 auto',
  },
  dot: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    transition: 'background 0.2s',
  },
  dotLabel: {
    fontSize: 11,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  connector: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    margin: '0 4px',
    marginBottom: 22,
    transition: 'background 0.2s',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    boxShadow: '0 2px 16px rgba(26,51,100,0.10)',
    marginBottom: 16,
  },
  stepTitle: {
    color: '#1a3364',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 24,
    paddingBottom: 14,
    borderBottom: '2px solid #dbeef8',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  btnBack: {
    padding: '12px 28px',
    background: 'transparent',
    border: '2px solid #1a3364',
    borderRadius: 8,
    color: '#1a3364',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnNext: {
    padding: '12px 32px',
    background: '#1a3364',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSaveDraft: {
    padding: '12px 22px',
    background: '#eef6fb',
    border: '1px solid #b8cfe8',
    borderRadius: 8,
    color: '#1a3364',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
